import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, UserIcon, PhoneIcon, MapPinIcon, SparklesIcon, ClockIcon, AlertCircleIcon, LockIcon, MessageCircleIcon, Globe2Icon } from 'lucide-react';
const locations = ['Miami, FL - 175 SW 7th St #2009, Miami, FL 33130', 'New York, NY - 116 Central Park South, New York, NY 10019', 'New York Lipo - 58 E 79th St 4R, New York, NY 10075', 'Houston, TX - 2702 Westheimer Rd, Houston, TX 77098', 'Las Vegas, NV - 2451 W Horizon Ridge Pkwy Suite 130, Henderson, NV 89052', 'Phoenix, AZ - 10505 N 69th St Suite 1100, Scottsdale, AZ 85253'];
const treatments = ['Lipo 360', 'BBL (Brazilian Butt Lift)', 'Tummy Tuck', 'Mommy Makeover', 'Face Tightening / Dermalift', 'Weight Loss (Ozempic/Semaglutide)', 'Laser Lipo', 'Skin Rejuvenation', 'Body Contouring', 'Breast Augmentation', 'Other'];
const languages = ['Spanish', 'English', 'Portuguese', 'Other'];
type LeadPayload = {
  name: string;
  phone: string;
  location: string;
  treatment: string;
  language: string;
  whatsappOptIn: boolean;
};
const WEBHOOK_URL = 'https://script.google.com/macros/s/AKfycbzEwa9X1kD_eQs6LTVlbm-Ut2f2oDZ3JW54lhUvEmTUmwEBSENr43SNCp12EyuCftLp/exec';
export function LeadForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    city: '',
    treatment: '',
    language: '',
    whatsappOptIn: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 59,
    seconds: 59
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return {
            ...prev,
            seconds: prev.seconds - 1
          };
        } else if (prev.minutes > 0) {
          return {
            hours: prev.hours,
            minutes: prev.minutes - 1,
            seconds: 59
          };
        } else if (prev.hours > 0) {
          return {
            hours: prev.hours - 1,
            minutes: 59,
            seconds: 59
          };
        }
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.phone.trim()) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 5000);
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const payload: LeadPayload = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        location: formData.city,
        treatment: formData.treatment,
        language: formData.language,
        whatsappOptIn: formData.whatsappOptIn
      };
      console.log('Sending payload:', payload);
      console.log('Webhook URL:', WEBHOOK_URL);
      fetch(WEBHOOK_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      }).catch(err => {
        console.log('Fetch completed (no-cors mode, errors are expected)', err);
      });
      console.log('Request sent successfully (no-cors mode)');
      // Track Lead event in Meta Pixel with enhanced debugging
      console.log('🔍 Attempting to track Lead event...');
      console.log('🔍 window.fbq exists?', typeof window !== 'undefined' && typeof window.fbq !== 'undefined');
      if (typeof window !== 'undefined' && window.fbq) {
        const leadData = {
          source: 'Dermaclinic Landing',
          domain: window.location.hostname,
          language: formData.language,
          treatment: formData.treatment,
          value: 0,
          currency: 'USD'
        };
        console.log('✅ Tracking Lead event with data:', leadData);
        window.fbq('track', 'Lead', leadData);
        console.log('✅ Lead event tracked successfully!');
      } else {
        console.error('❌ Facebook Pixel (fbq) not found! Pixel may be blocked or not loaded.');
      }
      setSubmitStatus('success');
      setFormData({
        name: '',
        phone: '',
        city: '',
        treatment: '',
        language: '',
        whatsappOptIn: true
      });
      setTimeout(() => setSubmitStatus('idle'), 10000);
    } catch (error) {
      console.error('Unexpected error:', error);
      setSubmitStatus('success');
      setFormData({
        name: '',
        phone: '',
        city: '',
        treatment: '',
        language: '',
        whatsappOptIn: true
      });
      setTimeout(() => setSubmitStatus('idle'), 10000);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  return <section id="lead-form" className="w-full py-20 lg:py-24 bg-gradient-to-br from-white via-brand-aqua/10 to-white relative overflow-hidden" data-id="element-162">
      <div className="absolute inset-0" data-id="element-163">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-gold/10 rounded-full blur-3xl" data-id="element-164" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-brand-primary/10 rounded-full blur-3xl" data-id="element-165" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-166">
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="max-w-3xl mx-auto mb-12" data-id="element-167">
          <div className="relative bg-gradient-to-r from-brand-navy via-brand-deep to-brand-navy rounded-2xl p-4 shadow-2xl border border-brand-gold/30 overflow-hidden" data-id="element-168">
            <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" data-id="element-169" />
            <div className="absolute inset-0 bg-brand-gold/5 animate-pulse pointer-events-none" data-id="element-170" />

            <div className="relative flex items-center justify-center gap-3" data-id="element-171">
              <div className="flex-shrink-0" data-id="element-172">
                <div className="w-8 h-8 rounded-full bg-brand-gold/20 backdrop-blur-sm flex items-center justify-center border border-brand-gold/40" data-id="element-173">
                  <AlertCircleIcon className="w-5 h-5 text-brand-gold" strokeWidth={2} data-id="element-174" />
                </div>
              </div>
              <p className="text-white font-bold text-base sm:text-lg tracking-wide" data-id="element-175">
                Only 7 VIP Spots Available This Week — Book Yours Today
              </p>
            </div>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start" data-id="element-176">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-177">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-gold/10 rounded-full mb-6" data-id="element-178">
              <SparklesIcon className="w-4 h-4 text-brand-gold" data-id="element-179" />
              <span className="text-brand-gold font-semibold text-sm uppercase tracking-wider" data-id="element-180">
                Free Consultation
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-navy mb-6 leading-tight" data-id="element-181">
              Book Your{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-primary to-brand-gold" data-id="element-182">
                Free Evaluation
              </span>
            </h2>
            <p className="text-xl text-gray-600 mb-10 leading-relaxed" data-id="element-183">
              Limited spots due to high demand. Take the first step toward your
              transformation.
            </p>

            <div className="bg-gradient-to-br from-brand-navy via-brand-deep to-brand-navy rounded-3xl p-8 mb-10 shadow-2xl border border-brand-gold/20" data-id="element-184">
              <div className="flex items-center gap-3 mb-6" data-id="element-185">
                <ClockIcon className="w-6 h-6 text-brand-gold" data-id="element-186" />
                <p className="text-white font-bold text-lg" data-id="element-187">
                  This offer expires in:
                </p>
              </div>
              <div className="grid grid-cols-3 gap-4" data-id="element-188">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 text-center border border-brand-gold/20" data-id="element-189">
                  <div className="text-4xl font-bold text-brand-gold mb-2" data-id="element-190">
                    {String(timeLeft.hours).padStart(2, '0')}
                  </div>
                  <div className="text-white/80 text-sm font-medium" data-id="element-191">Hours</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 text-center border border-brand-gold/20" data-id="element-192">
                  <div className="text-4xl font-bold text-brand-gold mb-2" data-id="element-193">
                    {String(timeLeft.minutes).padStart(2, '0')}
                  </div>
                  <div className="text-white/80 text-sm font-medium" data-id="element-194">
                    Minutes
                  </div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 text-center border border-brand-gold/20" data-id="element-195">
                  <div className="text-4xl font-bold text-brand-gold mb-2" data-id="element-196">
                    {String(timeLeft.seconds).padStart(2, '0')}
                  </div>
                  <div className="text-white/80 text-sm font-medium" data-id="element-197">
                    Seconds
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4 mb-8" data-id="element-198">
              <div className="flex items-center gap-3 p-4 bg-white rounded-2xl shadow-lg border border-brand-gold/10" data-id="element-199">
                <CheckCircleIcon className="w-6 h-6 text-brand-gold" data-id="element-200" />
                <span className="text-gray-700 font-semibold" data-id="element-201">
                  Financing Available
                </span>
              </div>
              <div className="flex items-center gap-3 p-4 bg-white rounded-2xl shadow-lg border border-brand-gold/10" data-id="element-202">
                <CheckCircleIcon className="w-6 h-6 text-brand-gold" data-id="element-203" />
                <span className="text-gray-700 font-semibold" data-id="element-204">
                  FDA-Approved Safe Procedures
                </span>
              </div>
              <div className="flex items-center gap-3 p-4 bg-white rounded-2xl shadow-lg border border-brand-gold/10" data-id="element-205">
                <CheckCircleIcon className="w-6 h-6 text-brand-gold" data-id="element-206" />
                <span className="text-gray-700 font-semibold" data-id="element-207">
                  Same-Day Appointments Available
                </span>
              </div>
            </div>

            <div className="hidden lg:block" data-id="element-208">
              <img src="https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=800&h=600&fit=crop" alt="Transform your body - Achieve your dream physique" className="rounded-3xl shadow-2xl ring-2 ring-brand-gold/20 object-cover w-full h-[400px]" data-id="element-209" />
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-210">
            <AnimatePresence mode="wait" data-id="element-211">
              {submitStatus === 'success' ? <motion.div key="success" initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.9
            }} transition={{
              duration: 0.4
            }} className="bg-white rounded-3xl shadow-2xl p-10 border-4 border-green-300 ring-4 ring-green-100" data-id="element-212">
                  <div className="text-center" data-id="element-213">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500 rounded-full mb-6 shadow-xl" data-id="element-214">
                      <CheckCircleIcon className="w-12 h-12 text-white" strokeWidth={2.5} data-id="element-215" />
                    </div>

                    <h3 className="text-3xl font-bold text-green-800 mb-4" data-id="element-216">
                      🎉 Thank you!
                    </h3>

                    <p className="text-lg text-green-700 font-medium mb-4 leading-relaxed" data-id="element-217">
                      Your request was successfully submitted.
                    </p>

                    <p className="text-base text-gray-700 mb-4 leading-relaxed" data-id="element-218">
                      A Dermaclinic specialist will reach out to you shortly via
                      WhatsApp or phone call to confirm your consultation.
                    </p>

                    <p className="text-base text-gray-600 leading-relaxed" data-id="element-219">
                      We look forward to helping you achieve your dream body! ✨
                    </p>
                  </div>
                </motion.div> : <motion.form key="form" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.3
            }} onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-2xl p-10 border-4 border-brand-primary/20 ring-4 ring-brand-gold/10" data-id="element-220">
                  <AnimatePresence data-id="element-221">
                    {submitStatus === 'error' && <motion.div initial={{
                  opacity: 0,
                  scale: 0.95,
                  y: -10
                }} animate={{
                  opacity: 1,
                  scale: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  scale: 0.95,
                  y: -10
                }} transition={{
                  duration: 0.3
                }} className="mb-6 bg-red-50 border-2 border-red-300 rounded-2xl p-5" data-id="element-222">
                        <p className="text-red-700 font-bold text-center text-base" data-id="element-223">
                          ❌ There was a problem submitting your request. Please
                          try again.
                        </p>
                      </motion.div>}
                  </AnimatePresence>

                  <div className="text-center mb-8" data-id="element-224">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-brand-primary to-brand-gold rounded-3xl mb-4 shadow-lg" data-id="element-225">
                      <SparklesIcon className="w-8 h-8 text-white" data-id="element-226" />
                    </div>
                    <h3 className="text-2xl font-bold text-brand-navy mb-2" data-id="element-227">
                      Reserve Your Spot
                    </h3>
                    <p className="text-gray-600" data-id="element-228">
                      Response in less than 24 hours
                    </p>
                  </div>

                  <div className="space-y-5" data-id="element-229">
                    <div data-id="element-230">
                      <label htmlFor="name" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-231">
                        Full Name <span className="text-red-500" data-id="element-232">*</span>
                      </label>
                      <div className="relative" data-id="element-233">
                        <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-234" />
                        <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Your full name" className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold transition-all duration-300" required disabled={isSubmitting} data-id="element-235" />
                      </div>
                    </div>

                    <div data-id="element-236">
                      <label htmlFor="phone" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-237">
                        Phone Number <span className="text-red-500" data-id="element-238">*</span>
                      </label>
                      <div className="relative" data-id="element-239">
                        <PhoneIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-240" />
                        <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="(555) 123-4567" className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold transition-all duration-300" required disabled={isSubmitting} data-id="element-241" />
                      </div>
                    </div>

                    <div data-id="element-242">
                      <label htmlFor="city" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-243">
                        Preferred Location{' '}
                        <span className="text-red-500" data-id="element-244">*</span>
                      </label>
                      <div className="relative" data-id="element-245">
                        <MapPinIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-246" />
                        <select id="city" name="city" value={formData.city} onChange={handleChange} className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold transition-all duration-300 appearance-none cursor-pointer" required disabled={isSubmitting} data-id="element-247">
                          <option value="" data-id="element-248">Select a location</option>
                          {locations.map(location => <option key={location} value={location} data-id="element-249">
                              {location}
                            </option>)}
                        </select>
                      </div>
                    </div>

                    <div data-id="element-250">
                      <label htmlFor="treatment" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-251">
                        Treatment of Interest{' '}
                        <span className="text-red-500" data-id="element-252">*</span>
                      </label>
                      <div className="relative" data-id="element-253">
                        <SparklesIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-254" />
                        <select id="treatment" name="treatment" value={formData.treatment} onChange={handleChange} className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold transition-all duration-300 appearance-none cursor-pointer" required disabled={isSubmitting} data-id="element-255">
                          <option value="" data-id="element-256">Select a treatment</option>
                          {treatments.map(treatment => <option key={treatment} value={treatment} data-id="element-257">
                              {treatment}
                            </option>)}
                        </select>
                      </div>
                    </div>

                    <div data-id="element-258">
                      <label htmlFor="language" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-259">
                        Preferred Language{' '}
                        <span className="text-red-500" data-id="element-260">*</span>
                      </label>
                      <div className="relative" data-id="element-261">
                        <Globe2Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-262" />
                        <select id="language" name="language" value={formData.language} onChange={handleChange} className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold transition-all duration-300 appearance-none cursor-pointer" required disabled={isSubmitting} data-id="element-263">
                          <option value="" data-id="element-264">Select your language</option>
                          {languages.map(language => <option key={language} value={language} data-id="element-265">
                              {language}
                            </option>)}
                        </select>
                      </div>
                    </div>

                    <div className="bg-[#25D366]/10 rounded-2xl p-4 border-2 border-[#25D366]/30" data-id="element-266">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-267">
                        <input type="checkbox" id="whatsappOptIn" name="whatsappOptIn" checked={formData.whatsappOptIn} onChange={handleChange} className="mt-1 w-5 h-5 text-[#25D366] border-gray-300 rounded focus:ring-[#25D366]" required disabled={isSubmitting} data-id="element-268" />
                        <div className="flex-1" data-id="element-269">
                          <div className="flex items-center gap-2 mb-1" data-id="element-270">
                            <MessageCircleIcon className="w-5 h-5 text-[#25D366]" data-id="element-271" />
                            <span className="text-sm font-bold text-gray-800" data-id="element-272">
                              Confirm via WhatsApp{' '}
                              <span className="text-red-500" data-id="element-273">*</span>
                            </span>
                          </div>
                          <p className="text-xs text-gray-600" data-id="element-274">
                            I agree to receive confirmation and reminders via
                            WhatsApp
                          </p>
                        </div>
                      </label>
                    </div>

                    <motion.button type="submit" disabled={isSubmitting} whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} className="w-full py-4 bg-gradient-to-r from-brand-primary via-brand-gold to-brand-primary text-white font-bold text-base rounded-2xl transition-all duration-300 shadow-2xl shadow-brand-gold/40 hover:shadow-brand-gold/60 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden group" data-id="element-275">
                      <span className="relative z-10 flex items-center justify-center gap-2" data-id="element-276">
                        {isSubmitting ? <>
                            <ClockIcon className="w-5 h-5 animate-spin" data-id="element-277" />
                            Sending...
                          </> : <>
                            <SparklesIcon className="w-5 h-5" data-id="element-278" />
                            Book Free Consultation
                            <span className="text-lg" data-id="element-279">→</span>
                          </>}
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-brand-deep to-brand-primary transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" data-id="element-280" />
                    </motion.button>

                    <p className="text-center text-sm text-red-600 font-semibold animate-pulse" data-id="element-281">
                      ⏰ Last spots available today
                    </p>
                  </div>

                  <div className="mt-6 flex flex-wrap justify-center gap-4 text-xs" data-id="element-282">
                    <span className="flex items-center gap-1.5 text-gray-600" data-id="element-283">
                      <CheckCircleIcon className="w-4 h-4 text-brand-gold" data-id="element-284" />
                      <span className="font-medium" data-id="element-285">24h Response</span>
                    </span>
                    <span className="flex items-center gap-1.5 text-gray-600" data-id="element-286">
                      <LockIcon className="w-4 h-4 text-brand-gold" data-id="element-287" />
                      <span className="font-medium" data-id="element-288">100% Confidential</span>
                    </span>
                    <span className="flex items-center gap-1.5 text-gray-600" data-id="element-289">
                      <CheckCircleIcon className="w-4 h-4 text-brand-gold" data-id="element-290" />
                      <span className="font-medium" data-id="element-291">No Obligation</span>
                    </span>
                  </div>
                </motion.form>}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>;
}